<script lang="ts" context="module">
  import Tooltip from "@ui/Tooltip.svelte";
  import type { TableOfContentItem } from "./types";
</script>

<script lang="ts">
  import { isRTL } from "sbelt/text";
  import { onMount } from "svelte";

  export let tocItems: TableOfContentItem[] = [];
  export let showScrollProgressIndicator: boolean = false;
  let expandTOC: boolean = true;
  let maxHeight: number;

  onMount(() => {
    const headerSlot = document.querySelector("[slot=header]") as HTMLElement;
    const headerElement = headerSlot?.parentElement || headerSlot;
    maxHeight = headerElement.offsetTop + 86;
    const brandElement = document.querySelector(".brandon") as HTMLElement;
    if (brandElement) {
      maxHeight += brandElement.offsetHeight;
    }
  });

  function scrollMe(linkId: string) {
    document.querySelector(`${linkId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    // In order to change the URL without actually redirecting and reloading the page,
    // We use the history for that...
    // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onpopstate
    window.history.pushState({ id: linkId }, "", `${document.location.href.split("#")[0]}${linkId === "html" ? "" : linkId}`);
  }
</script>

<nav class="w-56 rounded bg-white bg-opacity-90" aria-label="Table of contents">
  <h2 class="relative flex items-center justify-between border-b border-nxgray-100 px-2.5 py-2 text-14 font-semibold">
    <div
      id="scroll-progress-indicator"
      class="absolute left-0 top-0 h-full w-full origin-top-left bg-gray-700 opacity-20"
      class:visible={showScrollProgressIndicator}
      class:invisible={!showScrollProgressIndicator}>
    </div>
    Table of Contents
    <span class="-ml-1 mr-1 leading-none">
      <button
        on:click|preventDefault|stopPropagation={() => scrollMe("html")}
        class="focusable material-icons notranslate text-18"
        aria-label="Scroll to the top of the newsletter"
        >vertical_align_top
      </button>
      <div class="hidden md:block">
        <Tooltip>Scroll to the top of the newsletter</Tooltip>
      </div>
    </span>
  </h2>
  {#if expandTOC}
    <ol class="flex h-fit w-full flex-col gap-y-1 overflow-y-auto p-1.5" style="max-height:{`calc(100vh - ${maxHeight}px)`}">
      {#each tocItems as tocItem}
        {@const linkId = `#${tocItem.id}`}
        {@const haveLongWord = tocItem.caption.split(" ").findIndex((word) => word.length > 24) >= 0}
        <li class="w-full">
          <a
            class="focusable group flex w-full items-start p-1 ring-offset-0 focus:ring-offset-2"
            href={linkId}
            on:click|preventDefault={() => scrollMe(linkId)}
            ><span
              class="w-full rounded p-1.5 text-13 font-semibold leading-snug text-nxgray-700 group-focus-within:!bg-nxgray-50 group-hover:bg-nxgray-50"
              class:break-all={haveLongWord}
              dir={isRTL(tocItem.caption) ? "rtl" : "ltr"}>{tocItem.caption}</span
            ></a>
        </li>
      {:else}
        <li class="px-2.5 py-2 border-t">Nothing to show</li>
      {/each}
    </ol>
  {/if}
</nav>

<style>
  #scroll-progress-indicator {
    display: none;
  }
  @supports (scroll-timeline: --page-scroll) {
    :global(html) {
      scroll-timeline: --page-scroll block;
    }

    @keyframes grow-progress {
      from {
        transform: scaleX(0);
      }
      to {
        transform: scaleX(1);
      }
    }

    #scroll-progress-indicator {
      display: block;
      animation: grow-progress auto linear;
      animation-timeline: --page-scroll;
    }
  }
</style>
